function hexToRgb(hex) {
  const clean = hex.replace("#", "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  const num = parseInt(full, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function rgbToHex(r, g, b) {
  const toHex = (v) =>
    Math.round(Math.min(255, Math.max(0, v)))
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      default:
        h = ((r - g) / d + 4) / 6;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToRgb(h, s, l) {
  h /= 360;
  s /= 100;
  l /= 100;
  if (s === 0) {
    const v = l * 255;
    return { r: v, g: v, b: v };
  }

  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: hue2rgb(p, q, h + 1 / 3) * 255,
    g: hue2rgb(p, q, h) * 255,
    b: hue2rgb(p, q, h - 1 / 3) * 255,
  };
}

function shade(hex, lightnessDelta, saturationDelta = 0) {
  const { r, g, b } = hexToRgb(hex);
  const { h, s, l } = rgbToHsl(r, g, b);
  const newS = Math.min(100, Math.max(0, s + saturationDelta));
  const newL = Math.min(100, Math.max(0, l + lightnessDelta));
  const rgb = hslToRgb(h, newS, newL);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

export function normalizeHex(input) {
  if (!input || typeof input !== "string") return null;
  const trimmed = input.trim();
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(withHash)) return null;
  if (withHash.length === 4) {
    return (
      "#" +
      withHash
        .slice(1)
        .split("")
        .map((c) => c + c)
        .join("")
    ).toLowerCase();
  }
  return withHash.toLowerCase();
}

const MAX_PICKABLE_LIGHTNESS = 45;

export function clampToDark(hex) {
  const { r, g, b } = hexToRgb(hex);
  const { h, s, l } = rgbToHsl(r, g, b);
  if (l <= MAX_PICKABLE_LIGHTNESS) return hex.toLowerCase();
  const rgb = hslToRgb(h, s, MAX_PICKABLE_LIGHTNESS);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

// shadcn-ui reads its colours as bare "h s% l%" triplets so Tailwind can wrap
// them in hsl() and vary the alpha (`bg-ui-primary/90`).
function hslTriplet(hex) {
  const { r, g, b } = hexToRgb(hex);
  const { h, s, l } = rgbToHsl(r, g, b);
  const round = (v) => Math.round(v * 10) / 10;
  return `${round(h)} ${round(s)}% ${round(l)}%`;
}

function tint(hex, lightness, saturationDelta = 0) {
  const { r, g, b } = hexToRgb(hex);
  const { h, s } = rgbToHsl(r, g, b);
  const newS = Math.min(100, Math.max(0, s + saturationDelta));
  const rgb = hslToRgb(h, newS, lightness);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

export function deriveTheme(base) {
  return {
    buttonBg: base,
    tableHeaderBg: base,
    buttonText: shade(base, 88, -20),
    tableHeaderText: shade(base, 88, -20),
    buttonHover: shade(base, 22, -10),
    // Light tint of the chosen color, for hover backgrounds on outline-style buttons
    buttonHoverLight: tint(base, 92, -25),
    // Dark shade of the chosen color, for hover text/border on those same buttons
    brandHoverText: clampToDark(base),
  };
}

export function defaultBaseColor() {
  return process.env.VUE_APP_BRAND_COLOR;
}

const DEFAULT_NAV_ACCENT = "#e2b745";

const FOCUS_RING = "#2563eb";

export function applyThemeColor(base) {
  const safeBase = normalizeHex(base) || defaultBaseColor();
  const theme = deriveTheme(safeBase);
  const isDefaultBase = safeBase === normalizeHex(defaultBaseColor());
  const navAccent = isDefaultBase ? DEFAULT_NAV_ACCENT : theme.buttonHover;

  const { r, g, b } = hexToRgb(safeBase);

  const root = document.documentElement;
  root.style.setProperty("--button", theme.buttonBg);

  root.style.setProperty("--brandRgb", `${r}, ${g}, ${b}`);
  root.style.setProperty("--brand", theme.tableHeaderBg);
  root.style.setProperty("--buttonText", theme.buttonText);
  root.style.setProperty("--tableHeaderText", theme.tableHeaderText);
  root.style.setProperty("--buttonHover", theme.buttonHover);
  root.style.setProperty("--buttonHoverLight", theme.buttonHoverLight);
  root.style.setProperty("--brandHoverText", theme.brandHoverText);
  root.style.setProperty("--navAccent", navAccent);
  root.style.setProperty("--navHoverBg", shade(navAccent, 30, -20));

  // Keep shadcn's primary in step with the tenant brand, so `bg-ui-primary`
  // matches `bg-[var(--brand)]` without every call site restating the colour.
  root.style.setProperty("--primary", hslTriplet(safeBase));
  root.style.setProperty("--primary-foreground", hslTriplet(theme.buttonText));

  const focus = hexToRgb(FOCUS_RING);
  root.style.setProperty("--focusRing", FOCUS_RING);
  root.style.setProperty(
    "--focusRingRgb",
    `${focus.r}, ${focus.g}, ${focus.b}`
  );
  root.style.setProperty("--ring", hslTriplet(FOCUS_RING));
}

export function resetThemeColor() {
  applyThemeColor(defaultBaseColor());
}

export function hexToRgba(hex, alpha = 1) {
  const normalized = normalizeHex(hex) || "#1d4d90";
  const { r, g, b } = hexToRgb(normalized);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function getThemeColor(store, role = "Examiner") {
  if (store && store.getters && store.getters.themeColors) {
    const color = store.getters.themeColors[role];
    if (color) return normalizeHex(color);
  }
  if (typeof window !== "undefined") {
    const cssVar = getComputedStyle(document.documentElement)
      .getPropertyValue("--brand")
      .trim();
    if (cssVar) return normalizeHex(cssVar);
  }
  return defaultBaseColor() || "#1d4d90";
}
